import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaUsers, FaClock } from "react-icons/fa";
import { Reveal } from "../../../components/common";

const Counter = ({ end, duration = 1800, suffix = "", start = false }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!start) return undefined;
        let startTime;
        let animationFrame;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const percentage = Math.min((timestamp - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - percentage, 3);
            setCount(Math.floor(eased * end));
            if (percentage < 1) {
                animationFrame = requestAnimationFrame(animate);
            }
        };

        animationFrame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrame);
    }, [end, duration, start]);

    return <span>{count}{suffix}</span>;
};

const STATS = [
    { icon: FaCheckCircle, value: 120, suffix: "+", label: "Projects completed", note: "Delivered on time, every time" },
    { icon: FaUsers, value: 85, suffix: "+", label: "Happy clients", note: "Relationships that last" },
    { icon: FaClock, value: 5, suffix: "+", label: "Years of experience", note: "A proven track record" },
];

const StatsSection = () => {
    const [statsVisible, setStatsVisible] = useState(false);

    useEffect(() => {
        const el = document.getElementById('stats-section');
        if (!el || typeof IntersectionObserver === "undefined") {
            setStatsVisible(true);
            return undefined;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setStatsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.25 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="stats-section" className="bleed relative overflow-hidden bg-designer-dark">
            <div className="absolute inset-0 texture-grid-dark opacity-70" aria-hidden="true" />
            <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[42rem] h-[20rem] rounded-full bg-designer-primary/15 blur-[110px] pointer-events-none"
                aria-hidden="true"
            />

            <div className="relative container-responsive py-14 md:py-20">
                <div className="grid gap-10 lg:grid-cols-[0.9fr_1.4fr] lg:items-center">
                    <div>
                        <Reveal>
                            <div className="flex items-center gap-3">
                                <span className="tick" aria-hidden="true" />
                                <p className="text-xs font-bold tracking-[0.25em] uppercase text-white/60">
                                    Our impact
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={90}>
                            <h2 className="text-balance mt-4 font-display font-extrabold tracking-tight leading-[1.05] text-3xl sm:text-4xl text-white">
                                Numbers that speak louder than promises
                            </h2>
                        </Reveal>
                        <Reveal delay={160}>
                            <p className="mt-4 text-white/60 leading-relaxed max-w-md">
                                Every project is a partnership. Here's what 5+ years of showing up for our clients adds up to.
                            </p>
                        </Reveal>
                    </div>

                    <dl className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-0 sm:divide-x sm:divide-white/10">
                        {STATS.map((stat, i) => (
                            <Reveal key={stat.label} delay={i * 120} className="sm:px-8 sm:first:pl-0 sm:last:pr-0">
                                <div className="flex sm:block items-center gap-5">
                                    <stat.icon className="w-7 h-7 text-designer-primary shrink-0" aria-hidden="true" />
                                    <dd className="font-display font-extrabold text-5xl md:text-6xl text-white leading-none tabular-nums">
                                        <Counter end={stat.value} suffix={stat.suffix} start={statsVisible} />
                                    </dd>
                                </div>
                                <dt className="mt-3 text-sm font-bold text-white">{stat.label}</dt>
                                <dd className="mt-1 text-[13px] text-white/50">{stat.note}</dd>
                            </Reveal>
                        ))}
                    </dl>
                </div>
            </div>
        </section>
    );
};

export default StatsSection;
